import queryString from 'query-string';
import type { ParsedQuery } from 'query-string';
import { FILEEE_DOMAINS, getBaseDomainUrl, isProductionEnv } from './utils';

/**
 * A query string can carry a key more than once (`?tag=a&tag=b`) or with no value
 * at all (`?webview`). query-string reflects that: a value is `string`, `null`, or
 * an array of those. Almost every caller wants a single string, so `getSearchParams`
 * collapses to one, and `getRawSearchParams` exposes the parsed form untouched for
 * the few callers that forward the whole query string on.
 */
export type SearchParams = Record<string, string>;

/**
 * Collapses one parsed value the way `URLSearchParams.get()` does: the first
 * occurrence wins, and a valueless key reads as an empty string.
 */
function toSingleValue(value: ParsedQuery[string]): string {
  const first = Array.isArray(value) ? value[0] : value;
  return first ?? '';
}

function collapse(parsed: ParsedQuery): SearchParams {
  return Object.keys(parsed).reduce((acc, key) => {
    acc[key] = toSingleValue(parsed[key]);
    return acc;
  }, {} as SearchParams);
}

/**
 * The parsed query string with repeated and valueless keys preserved. Use this only
 * when re-serialising the query string, so forwarded URLs keep every parameter.
 */
export function getRawSearchParams(): ParsedQuery {
  return queryString.parse(window.location.search) || {};
}

/* for both query params and hash params */
export function getSearchParams(): SearchParams {
  return collapse(getRawSearchParams());
}

export function getSearchOnlyParams(): SearchParams {
  return collapse(getRawSearchParams());
}

export function getBaseReloadLocation() {
  return '';
}

export function getLocationPathName() {
  return window.location.pathname;
}

export function getHostName() {
  return window.location.hostname;
}

/**
 * Exported only for testing
 * @param {string} shareId
 * @returns {string | null}
 */
export function getLinkFromShareId(shareId) {
  if (shareId) {
    const currentDomain = getBaseDomainUrl();
    if (FILEEE_DOMAINS.has(currentDomain)) {
      return `${currentDomain}/shared/${shareId}`;
    } else {
      return `https://my.fileee.com/shared/${shareId}`;
    }
  } else {
    return null;
  }
}

export function getSupportActionLink() {
  if (isProductionEnv()) {
    // https://file.ee/serviceanfrage
    return '/actions/50ac222b0cf2f924c9ccb919-5ef1fbebe8dd85567073794d';
  } else {
    return '/actions/50ac222b0cf2f924c9ccb919-5ef1fbebe8dd85567073794d';
  }
}

export function getBaseRelativeUrl() {
  return '/';
}

/**
 *
 * @param {string} link Relative Link
 */
export function fullPageRedirectTo(link) {
  window.location.href = `${getBaseReloadLocation()}${link}`;
}
